const toStartOfDayIso = (date) => {
  const boundary = new Date(date);
  boundary.setHours(0, 0, 0, 0);
  return boundary.toISOString();
};

const toEndOfDayIso = (date) => {
  const boundary = new Date(date);
  boundary.setHours(23, 59, 59, 999);
  return boundary.toISOString();
};

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const earliestManagementDate = () => new Date(1970, 0, 1);

const parseLocalDate = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Converts the management-page date presets into inclusive local-day bounds.
 * Full timestamps are important because a date-only end bound represents
 * midnight and otherwise excludes records created later on that date.
 */
export const computeManagementDateParams = (filters, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  switch (filters?.dateRange) {
    case 'today':
      return {
        startDate: toStartOfDayIso(today),
        endDate: toEndOfDayIso(today),
      };

    case 'past': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        startDate: toStartOfDayIso(earliestManagementDate()),
        endDate: toEndOfDayIso(yesterday),
      };
    }

    case 'this_week': {
      const endOfRange = new Date(today);
      endOfRange.setDate(endOfRange.getDate() + 6);
      return {
        startDate: toStartOfDayIso(today),
        endDate: toEndOfDayIso(endOfRange),
      };
    }

    case 'this_month': {
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        startDate: toStartOfDayIso(today),
        endDate: toEndOfDayIso(endOfMonth),
      };
    }

    case 'custom': {
      const start = parseLocalDate(filters?.startDate);
      const end = parseLocalDate(filters?.endDate);
      return {
        startDate: start ? toStartOfDayIso(start) : undefined,
        endDate: end ? toEndOfDayIso(end) : undefined,
      };
    }

    default:
      return { startDate: toStartOfDayIso(today), endDate: undefined };
  }
};

/**
 * Booking endpoints accept calendar dates rather than timestamp boundaries.
 * Past also receives an explicit lower bound so the API cannot apply its
 * default upcoming start date alongside yesterday's end date.
 */
export const computeBookingDateParams = (filters, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  switch (filters?.dateRange) {
    case 'today': {
      const date = toLocalDateString(today);
      return { startDate: date, endDate: date };
    }

    case 'past': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        startDate: toLocalDateString(earliestManagementDate()),
        endDate: toLocalDateString(yesterday),
      };
    }

    case 'this_week': {
      const endOfRange = new Date(today);
      endOfRange.setDate(endOfRange.getDate() + 6);
      return {
        startDate: toLocalDateString(today),
        endDate: toLocalDateString(endOfRange),
      };
    }

    case 'this_month': {
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        startDate: toLocalDateString(today),
        endDate: toLocalDateString(endOfMonth),
      };
    }

    case 'custom':
      return {
        startDate: parseLocalDate(filters?.startDate) ? filters.startDate : undefined,
        endDate: parseLocalDate(filters?.endDate) ? filters.endDate : undefined,
      };

    default:
      return { startDate: toLocalDateString(today), endDate: undefined };
  }
};
